import { Component, effect, inject, input, Input, signal } from "@angular/core";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import {
    BalanceAccountCard,
    BalanceAccountCardComponent
} from "./balance-account-card/balance-account-card.component";
import { DeviceService } from "@app/shared/services/device.service";
import { CompanyEntity } from "@arch/domain/entity";
import { VisualizationOptionsType } from "@arch/_shared/types/options-visualization";
import { GetBalanceDataController } from "@arch/presentation/controllers/";
import {
    trigger,
    transition,
    style,
    animate,
    query,
    stagger
} from "@angular/animations";

@Component({
    selector: "app-table-balance",
    imports: [
        SharedMaterialModule,
        SharedDsnModule,
        BalanceAccountCardComponent
    ],
    templateUrl: "./table-balance.component.html",
    styleUrl: "./table-balance.component.scss",
    animations: [
        trigger("balanceCardAnimation", [
            transition(":enter", [
                style({
                    opacity: 0,
                    transform: "translateY(-30px)"
                }),
                animate(
                    "300ms cubic-bezier(0.68, -0.55, 0.265, 1.55)",
                    style({
                        opacity: 1,
                        transform: "translateY(0)"
                    })
                )
            ]),
            transition(":leave", [
                animate(
                    "200ms cubic-bezier(0.68, -0.55, 0.265, 1.55)",
                    style({
                        opacity: 0,
                        transform: "translateY(-30px)"
                    })
                )
            ])
        ]),
        trigger("staggerAnimation", [
            transition("* => *", [
                query(
                    ":enter",
                    [
                        style({
                            opacity: 0,
                            transform: "translateY(-30px)"
                        }),
                        stagger("50ms", [
                            animate(
                                "300ms cubic-bezier(0.68, -0.55, 0.265, 1.55)",
                                style({
                                    opacity: 1,
                                    transform: "translateY(0)"
                                })
                            )
                        ])
                    ],
                    { optional: true }
                ),
                query(
                    ":leave",
                    [
                        stagger("30ms", [
                            animate(
                                "200ms cubic-bezier(0.68, -0.55, 0.265, 1.55)",
                                style({
                                    opacity: 0,
                                    transform: "translateY(-30px)"
                                })
                            )
                        ])
                    ],
                    { optional: true }
                )
            ])
        ])
    ]
})
export class TableBalanceComponent {
    @Input({ required: true }) company: CompanyEntity;
    @Input({ required: false }) pdf: boolean;
    @Input({ required: false }) indexInterval: {
        assets: number[];
        liabilities: number[];
    };

    public options = input.required<VisualizationOptionsType>();
    public loading = input.required<boolean>();

    private readonly deviceService = inject(DeviceService);
    private readonly getBalanceDataController = inject(
        GetBalanceDataController
    );
    public readonly isMobile = this.deviceService.getIsMobile();
    public assetsAccounts: BalanceAccountCard[] = [];
    public liabilitiesAccounts: BalanceAccountCard[] = [];
    public expandedAccountsAssets: string[] = [];
    public expandedAccountsLiabilities: string[] = [];
    public hideZeroAccountsAssets = signal(false);
    public hideZeroAccountsLiabilities = signal(false);

    constructor() {
        effect(() => {
            if (!this.options() || this.loading()) return null;
            const balanceData = this.getBalanceDataController.execute({
                options: this.options(),
                company: this.company,
                optionsConfig: {
                    calcAnalysisVertical:
                        this.options().balance?.analysisVertical,
                    calcAnalysisHorizontal:
                        this.options().balance?.analysisHorizontal
                }
            });
            let assetsAccounts = balanceData.assets.map((asset) => ({
                serie: {
                    _id: asset.serie.props._id,
                    name: asset.serie.props.name,
                    unit: asset.serie.props.unit
                },
                type: asset.config.type,
                base: asset.base,
                compound: asset.config.compound,
                comparation: asset.comparation,
                ah: asset.ah,
                isExpanded: true
            }));
            let liabilitiesAccounts = balanceData.liabilities.map(
                (liability) => ({
                    serie: {
                        _id: liability.serie.props._id,
                        name: liability.serie.props.name,
                        unit: liability.serie.props.unit
                    },
                    type: liability.config.type,
                    base: liability.base,
                    compound: liability.config.compound,
                    comparation: liability.comparation,
                    ah: liability.ah,
                    isExpanded: true
                })
            );
            if (this.indexInterval && this.pdf) {
                assetsAccounts = assetsAccounts.filter((account, index) =>
                    this.indexInterval.assets.includes(index)
                );
                liabilitiesAccounts = liabilitiesAccounts.filter(
                    (account, index) =>
                        this.indexInterval.liabilities.includes(index)
                );
            }
            if (this.hideZeroAccountsAssets()) {
                this.assetsAccounts = assetsAccounts.filter(
                    (account) => account.base.value !== 0
                );
            } else {
                this.assetsAccounts = assetsAccounts;
            }
            if (this.hideZeroAccountsLiabilities()) {
                this.liabilitiesAccounts = liabilitiesAccounts.filter(
                    (account) => account.base.value !== 0
                );
            } else {
                this.liabilitiesAccounts = liabilitiesAccounts;
            }
            const assetsIds = balanceData.assets.map(
                (asset) => asset.serie.props._id
            );
            const liabilitiesIds = balanceData.liabilities.map(
                (liability) => liability.serie.props._id
            );
            this.expandedAccountsAssets = [...assetsIds];
            this.expandedAccountsLiabilities = [...liabilitiesIds];
        });
    }

    public onExpandAssets(account: BalanceAccountCard): void {
        if (account.type === "main") {
            if (!account.isExpanded) {
                this.expandedAccountsAssets = [
                    account.serie._id,
                    ...account.compound
                ];
                const assetsAccounts = this.assetsAccounts.map((asset) => ({
                    ...asset,
                    isExpanded: true
                }));
                this.assetsAccounts = [...assetsAccounts];
            } else {
                this.expandedAccountsAssets = [account.serie._id];
            }
        } else {
            for (const id of account.compound) {
                if (this.expandedAccountsAssets.includes(id)) {
                    this.expandedAccountsAssets.splice(
                        this.expandedAccountsAssets.indexOf(id),
                        1
                    );
                } else {
                    this.expandedAccountsAssets.push(id);
                }
            }
        }
        account.isExpanded = !account.isExpanded;
    }

    public onExpandLiabilities(account: BalanceAccountCard): void {
        if (account.type === "main") {
            if (!account.isExpanded) {
                this.expandedAccountsLiabilities = [
                    account.serie._id,
                    ...account.compound
                ];
                const liabilitiesAccounts = this.liabilitiesAccounts.map(
                    (liability) => ({
                        ...liability,
                        isExpanded: true
                    })
                );
                this.liabilitiesAccounts = [...liabilitiesAccounts];
            } else {
                this.expandedAccountsLiabilities = [account.serie._id];
            }
        } else {
            for (const id of account.compound) {
                if (this.expandedAccountsLiabilities.includes(id)) {
                    this.expandedAccountsLiabilities.splice(
                        this.expandedAccountsLiabilities.indexOf(id),
                        1
                    );
                } else {
                    this.expandedAccountsLiabilities.push(id);
                }
            }
        }
        account.isExpanded = !account.isExpanded;
    }

    public onToggleZeroAccountsAssets(): void {
        this.hideZeroAccountsAssets.set(!this.hideZeroAccountsAssets());
    }

    public onToggleZeroAccountsLiabilities(): void {
        this.hideZeroAccountsLiabilities.set(
            !this.hideZeroAccountsLiabilities()
        );
    }
}
